import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConnectKitProvider, getDefaultConfig } from 'connectkit';
import React, { PropsWithChildren } from 'react';
import { Chain, Transport } from 'viem';
import { createConfig, http, WagmiProvider } from 'wagmi';
import { anvil } from 'wagmi/chains';
import { ETHEREAL_DEVNET_CHAIN, ETHEREAL_TESTNET_CHAIN } from './lib/chains';
import { environment, isDev } from './lib/config';
import walletModalTheme from './walletModalTheme.json';

const isTestnet = environment === 'testnet';
const localChains = isDev ? [anvil, ETHEREAL_DEVNET_CHAIN, ETHEREAL_TESTNET_CHAIN] : [];
const transports = isDev
  ? ({
      [ETHEREAL_TESTNET_CHAIN.id]: http(ETHEREAL_TESTNET_CHAIN.rpcUrls.default.http[0]),
      [ETHEREAL_DEVNET_CHAIN.id]: http(ETHEREAL_DEVNET_CHAIN.rpcUrls.default.http[0]),
      [anvil.id]: http('http://127.0.0.1:8545'),
    } satisfies Record<Chain['id'], Transport>)
  : isTestnet
    ? ({
        [ETHEREAL_TESTNET_CHAIN.id]: http(ETHEREAL_TESTNET_CHAIN.rpcUrls.default.http[0]),
      } satisfies Record<Chain['id'], Transport>)
    : ({
        [ETHEREAL_DEVNET_CHAIN.id]: http(ETHEREAL_DEVNET_CHAIN.rpcUrls.default.http[0]),
      } satisfies Record<Chain['id'], Transport>);

const config = createConfig(
  getDefaultConfig({
    // Your dApps chains
    // @ts-expect-error will move to privy soon so not spending time making types happy here
    chains: isDev ? [...localChains] : [isTestnet ? ETHEREAL_TESTNET_CHAIN : ETHEREAL_DEVNET_CHAIN],
    transports,
    // Most mobile wallets doesn't support custom network, this leads to errors so disabling for now.
    // walletConnectProjectId: walletConnectProjectId,
    walletConnectProjectId: '',

    // Required App Info
    appName: 'Ethereal',

    // Optional App Info
    appDescription: 'Trade with deep liquidity and low fees',
    appUrl: isDev ? 'http://localhost:5173' : 'https://app.etherealtest.net/',
    appIcon: '/logo.png', // your app's icon, no bigger than 1024x1024px (max. 1MB)
  })
);

// eslint-disable-next-line react-refresh/only-export-components
export const queryClient = new QueryClient();

export const Web3Provider: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <ConnectKitProvider
          customTheme={walletModalTheme}
          options={{
            initialChainId: isDev ? 0 : ETHEREAL_TESTNET_CHAIN.id,
            enforceSupportedChains: true,
          }}
        >
          {children}
        </ConnectKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

declare module 'wagmi' {
  interface Register {
    config: typeof config;
  }
}
